import cx from 'classnames';
import { ChartActionsComponentProps, ChartMetaFormField, MarRecord } from 'models/ui';
import { chartHelper } from 'helpers';
import { Box, ChartActions, ChartPermission, ErrorCard, ErrorFormField, SectionDivider, Sidebar } from 'components/common';
import { ELSFlex, ELSFlexItem } from 'components/common/els';
import { DOSE_MODE, FormField } from './constants';

export interface MarHoldAndDiscontinueFormSidebarViewProps {
  selectedDosageRecord: MarRecord;
  doseFormMode: string;
  formFieldMap: Map<string, ChartMetaFormField>;
  formSubmittedCount: number;
  chartActionsProps: ChartActionsComponentProps;
  onCloseClick: Function;
  isAuthor: boolean;
  isShowEHRMar?: boolean;
  sidebarProps: Function;
}

const MarHoldAndDiscontinueFormSidebarView = (props: MarHoldAndDiscontinueFormSidebarViewProps) => {
  const { selectedDosageRecord, doseFormMode, formFieldMap, formSubmittedCount, chartActionsProps, onCloseClick, isAuthor, sidebarProps } = props;
  const drugName = selectedDosageRecord?.drugName;
  const route = selectedDosageRecord?.route;
  const dose = selectedDosageRecord?.dose;
  const frequency = selectedDosageRecord?.frequency;
  const specialInstructions = selectedDosageRecord?.specialInstructions;
  const isHold = doseFormMode === DOSE_MODE.HOLD;
  // Hold and Discontinue render the same form against a different set of fields.
  const modeFields = isHold
    ? {
        date: FormField.HOLD_DATE,
        time: FormField.HOLD_TIME,
        timeOffset: FormField.HOLD_TIME_OFFSET,
        comments: FormField.HOLD_COMMENTS,
        firstInitial: FormField.HOLD_FIRST_INITIAL,
        lastInitial: FormField.HOLD_LAST_INITIAL
      }
    : {
        date: FormField.DISCONTINUE_DATE,
        time: FormField.DISCONTINUE_TIME,
        timeOffset: FormField.DISCONTINUE_TIME_OFFSET,
        comments: FormField.DISCONTINUE_COMMENTS,
        firstInitial: FormField.DISCONTINUE_FIRST_INITIAL,
        lastInitial: FormField.DISCONTINUE_LAST_INITIAL
      };
  const timeField = formFieldMap.get(isAuthor ? modeFields.timeOffset : modeFields.time);
  return (
    <Sidebar
      open={(isHold || doseFormMode === DOSE_MODE.DISCONTINUE) && !!selectedDosageRecord}
      onClose={() => chartHelper.handleCloseSidebar({ sidebarProps, onCloseClick })}
      sidebarProps={sidebarProps}
    >
      <Box mt3>
        <h2>{isHold ? 'Hold' : 'Discontinue'}</h2>
        <h3 className="u-els-margin-top u-els-margin-bottom-3o4">{drugName}</h3>
        {[route, dose, frequency].filter(Boolean).length > 0 && <span className="u-els-bold u-els-margin-right">{[route, dose, frequency].filter(Boolean).join(' · ')}</span>}
        {specialInstructions && <span className="u-els-italic u-els-color-n7">{specialInstructions}</span>}
      </Box>
      <SectionDivider />
      <ErrorCard chartMetaFormFields={formFieldMap} showErrorCard={formSubmittedCount > 0} className="u-els-margin-bottom" />
      <Box className="mar-dosage">
        <ELSFlex left wrap />
        <ELSFlex left wrap>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(modeFields.date)}
              formSubmittedCount={formSubmittedCount}
              className="u-els-margin-bottom-1x1o2"
              inputProps={{ isDisabled: true }}
            />
          </ELSFlexItem>
          <ELSFlexItem w="1o2" md="1o1">
            <ErrorFormField
              useDefaultMargin={false}
              formField={timeField}
              formSubmittedCount={formSubmittedCount}
              className={cx('u-els-margin-bottom-1x1o2', { 'mar-dosage__input-offset-time': isAuthor })}
            />
          </ELSFlexItem>
        </ELSFlex>
        <ErrorFormField formField={formFieldMap.get(modeFields.comments)} formSubmittedCount={formSubmittedCount} useDefaultMargin={false} className="u-els-margin-bottom-1x1o2" />
        <ELSFlex className="o-els-flex-layout-gutters-1o2">
          <ELSFlexItem>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(modeFields.firstInitial)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2 u-els-margin-right-2x"
            />
          </ELSFlexItem>
          <ELSFlexItem grow>
            <ErrorFormField
              useDefaultMargin={false}
              formField={formFieldMap.get(modeFields.lastInitial)}
              formSubmittedCount={formSubmittedCount}
              className="mar-dosage__initials u-els-margin-bottom-1x1o2"
            />
          </ELSFlexItem>
        </ELSFlex>
      </Box>
      <ChartPermission>{({ allowSaveFragment }) => allowSaveFragment && <ChartActions {...chartActionsProps} />}</ChartPermission>
    </Sidebar>
  );
};

MarHoldAndDiscontinueFormSidebarView.displayName = 'MarHoldAndDiscontinueFormSidebarView';
export default MarHoldAndDiscontinueFormSidebarView;
